import React from 'react';
import { css } from '@emotion/react';
import moment from 'moment';

import t from 'react-translate';

import { DataDownloadInfo, ReportStatus } from 'redux/schemas/api/custom-downloads';

import { quarterSpacing, halfSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { primary, success } from 'styles/global_defaults/colors';
import { config } from '@config/pendo.config.json';

import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';

export const formatReportDate = (date?: string | null, format = 'MOMENT.MONTH_DAY_YEAR_TIME') => (
  date ? moment(date).format(format) : ''
);

type DataDownloadsCardProps = {
  downloadInfo: DataDownloadInfo;
  type: 'dataDownload' | 'contentDownload';
  lastVisitedAt?: string
  onClick?: () => void;
  onViewDownloads?: () => void;
  generateReport?: () => void;
  generateContent?: () => void;
  cardOptions?: React.ReactNode;
};

const noop = () => {};
const styles = css`
  position: relative;
  min-height: 244px;

  .info {
    height: 90px;
    position: relative;
    overflow: hidden;
  }

  .icon-refresh {
    margin-top: ${quarterSpacing}px;
    margin-bottom: ${halfSpacing}px;
  }

  .generating {
    margin-top: -${halfSpacing}px;
    margin-bottom: ${quarterSpacing}px;
  }

  .generated-date {
    margin-bottom: ${threeQuartersSpacing}px;

    .icon {
      vertical-align: middle;
      margin: 0 ${halfSpacing}px;
      display: inline-block;
    }
  }

  .sftp-indicator {
    .img-circle {
      width: 8px;
      height: 8px;
      margin-right: ${quarterSpacing}px;
    }
  }

  .course-options {
    position: absolute;
    top: ${quarterSpacing}px;
    right: ${halfSpacing}px;

    .dropdown-menu:not(.navigation-menu) {
      min-width: 90px;
    }
  }
  .data-menu {
    .dropdown-menu:not(.navigation-menu) {
      min-width: 200px !important;
    }
  }

  .duplicating .icon-refresh {
    margin-top: 60px;
  }

  &.card {
    border-top: 1px solid ${primary};

    &.content-download {
      border-top: 1px solid ${success} !important;
    }

    .settings {
      visibility: hidden;
    }
  }

  .new-badge {
    position: absolute;
    top: ${quarterSpacing}px;
    left: 0;
    padding: 2px ${quarterSpacing}px;
    border-radius: 0;

    text-transform: uppercase;

    &.updated-badge {
      text-transform: none;
    }

    &.new-sftp {
      position: relative;
      margin-right: ${halfSpacing}px;
      top: 0;
    }
  }
`;

const DataDownloadsCard = ({
  downloadInfo,
  type,
  lastVisitedAt,
  onClick,
  onViewDownloads,
  generateReport = noop,
  generateContent = noop,
  cardOptions,
}: DataDownloadsCardProps) => {
  const { reportStatus } = downloadInfo;
  const isContentDownload = type === 'contentDownload';

  const hasRecentTimestamp = (
    !!lastVisitedAt
    && !!downloadInfo.reportTimestamp
    && lastVisitedAt < downloadInfo.reportTimestamp
    && !!downloadInfo.createdAt
  );
  const isNewerThanCreated = hasRecentTimestamp && downloadInfo.reportTimestamp! > downloadInfo.createdAt!;
  const isNew = !!downloadInfo.isDuplicate || (hasRecentTimestamp && !isNewerThanCreated);
  const isUpdated = isNewerThanCreated;

  const showViewDownloadsButton = (
    !isContentDownload
    && !downloadInfo.isDuplicatePlaceholder
    && !downloadInfo.deleteOlderReports
    && (downloadInfo.reportStatus === ReportStatus.GENERATED || downloadInfo.reportStatus === ReportStatus.FAILED)
  );

  const renderReportStatus = () => {
    if (downloadInfo.isDuplicatePlaceholder) {
      return null;
    }
    if (reportStatus === ReportStatus.GENERATING) {
      return (
        <div className='generating'>
          <NvIcon
            icon='refresh'
            size='small'
            className='color-primary spin'
          />
          <div className='condensed'>
            {t.CUSTOM_DOWNLOADS.PREPARING_DOWNLOAD()}
          </div>
          <div className='condensed'>
            {t.CUSTOM_DOWNLOADS.RECEIVE_EMAIL_WHEN_READY()}
          </div>
        </div>
      );
    }
    if (downloadInfo.generatedAt && reportStatus === ReportStatus.GENERATED) {
      return (
        <div className='generated-date'>
          <span className='card-title-xs'>
            {t.CUSTOM_DOWNLOADS.GENERATED_ON(formatReportDate(downloadInfo.generatedAt))}
          </span>
          {isContentDownload && (
            <NvTooltip
              text={t.CUSTOM_DOWNLOADS.REFRESH_TO_GET_LATEST_CONTENT()}
              placement='top'
            >
              <NvIcon
                icon='refresh'
                size='smallest'
                className='text-primary'
                altLabel={t.CUSTOM_DOWNLOADS.REFRESH_TO_GET_LATEST_CONTENT()}
                onClick={(e) => { e.stopPropagation(); generateContent(); }}
                data-qa={config.pendo.customDownloads.card.refreshContent}
              />
            </NvTooltip>
          )}
        </div>
      );
    }
    if (reportStatus === ReportStatus.FAILED) {
      return (
        <div className='generated-date'>
          <NvIcon icon='warning' size='smallest' className='text-danger' />
          <span className='card-title-xs'>
            {t.CUSTOM_DOWNLOADS.FAILED_TO_GENERATE()}
          </span>
        </div>
      );
    }
    if (reportStatus === ReportStatus.NOT_STARTED) {
      return (
        <div className={`generated-date${isContentDownload ? ' invisible' : ''}`}>
          <span className='card-title-xs'>{t.CUSTOM_DOWNLOADS.NO_DOWNLOAD_GENERATED()}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={`card d-flex flex-column align-items-center text-center px-2 pt-2 pb-4 mb-3 ${isContentDownload ? ' content-download' : ''} ${!onClick ? 'cursor-default ' : ''}`}
      css={styles}
    >
      <div className='align-self-end'>
        {cardOptions}
      </div>
      <ClickableContainer
        onClick={onClick}
        layoutOnly={!onClick}
        className={`d-flex justify-content-between flex-column flex-grow-1 align-items-center ${isContentDownload ? 'p-2' : 'pt-2'}`}
        data-qa={config.pendo.customDownloads.card.container}
      >
        {!isContentDownload && !downloadInfo.isDuplicatePlaceholder && (
          <div className='info'>
            <div className='card-title'>
              {downloadInfo.reportTitle}
              <div className='fade-out' />
            </div>
          </div>
        )}

        {isContentDownload && (
          <div className='info'>
            <div className='card-title'>{downloadInfo.contentType}</div>
            <div className='card-subtitle'>
              {downloadInfo.reportTitle}
              <div className='fade-out' />
            </div>
          </div>
        )}

        <div>
          {downloadInfo.isDuplicatePlaceholder && (
            <div className='duplicating'>
              <div className='card-title'>{t.CUSTOM_DOWNLOADS.DUPLICATING()}</div>
              <NvIcon
                icon='refresh'
                size='smallest'
                className='spin color-primary'
              />
            </div>
          )}

          {renderReportStatus()}

          {downloadInfo.isDailySftp && (
            <div className='sftp-indicator d-flex justify-content-center align-items-center mb-2'>
              <div className='img-circle bg-color-success' />
              <span className='card-title-xs'>
                {t.CUSTOM_DOWNLOADS.SFTP_ENABLED()}
              </span>
            </div>
          )}

          {showViewDownloadsButton && (
            <button
              type='button'
              className='btn btn-primary'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onViewDownloads?.();
              }}
              data-qa={config.pendo.customDownloads.card.viewDownloads}
            >
              {t.CUSTOM_DOWNLOADS.VIEW_DOWNLOADS()}
            </button>
          )}

          {!isContentDownload && reportStatus === ReportStatus.NOT_STARTED && !downloadInfo.isDuplicatePlaceholder && (
            <button
              type='button'
              className='btn btn-primary'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                generateReport();
              }}
              data-qa={config.pendo.customDownloads.card.generate}
            >
              {t.CUSTOM_DOWNLOADS.GENERATE()}
            </button>
          )}

          {isContentDownload && reportStatus === ReportStatus.GENERATED && downloadInfo.url && (
            <a
              className='btn btn-link blue-link'
              href={downloadInfo.url}
              target='_blank'
              rel='noreferrer'
              download
              onClick={(e) => e.stopPropagation()}
            >
              {t.CUSTOM_DOWNLOADS.DOWNLOAD_CONTENT()}
            </a>
          )}

          {isContentDownload
            && (reportStatus === ReportStatus.FAILED || reportStatus === ReportStatus.NOT_STARTED)
            && (
              <ClickableContainer
                className='btn btn-link blue-link'
                onClick={(e) => { e.stopPropagation(); generateContent(); }}
                data-qa={config.pendo.customDownloads.card.generateDownload}
              >
                {t.CUSTOM_DOWNLOADS.GENERATE_DOWNLOAD()}
              </ClickableContainer>
            )}
        </div>

        {isNew && (
          <div className='new-badge bg-success condensed label'>
            {t.CUSTOM_DOWNLOADS.NEW()}
          </div>
        )}
        {isUpdated && (
          <div className='new-badge updated-badge'>
            {t.CUSTOM_DOWNLOADS.UPDATED()}
          </div>
        )}
      </ClickableContainer>

    </div>
  );
};

export default DataDownloadsCard;
